import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Container, Grid, Paper, Stack, TextField, Typography,
} from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { v4 } from "uuid";
import { Header } from "../../components/Header";
import UserContext from "../../context/User";
import {
  restaurantForReservation,
  publicReservationSettings,
  reservationSlots,
  createWebReservation,
} from "../../apollo/server";

const RESTAURANT = gql`${restaurantForReservation}`;
const SETTINGS = gql`${publicReservationSettings}`;
const SLOTS = gql`${reservationSlots}`;
const CREATE = gql`${createWebReservation}`;

const ACCENT = "#FF3008";

// Dates are the restaurant's calendar days (YYYY-MM-DD). Arithmetic in UTC so
// the visitor's own timezone can never shift a day.
const pad = (n) => String(n).padStart(2, "0");
function addDaysKey(key, n) {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
function dayParts(key) {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const opts = { timeZone: "UTC" };
  return {
    week: dt.toLocaleDateString("en-US", { ...opts, weekday: "short" }),
    day: dt.toLocaleDateString("en-US", { ...opts, month: "short", day: "numeric" }),
    long: dt.toLocaleDateString("en-US", { ...opts, weekday: "long", month: "long", day: "numeric" }),
  };
}
const errText = (e) => e?.graphQLErrors?.[0]?.message || e?.message || "Something went wrong. Please try again.";

function SectionTitle({ n, icon: Icon, children }) {
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: "8px", bgcolor: "#fff1ee", color: ACCENT,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", fontWeight: 800,
      }}>
        {n}
      </Box>
      <Icon sx={{ fontSize: 18, color: "#64748b" }} />
      <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0f172a" }}>{children}</Typography>
    </Stack>
  );
}

function Pick({ active, disabled, onClick, children, sx }) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); } }}
      sx={{
        px: 1.75, py: 1, borderRadius: "12px", cursor: disabled ? "default" : "pointer",
        border: "1.5px solid", borderColor: active ? ACCENT : "#e2e8f0",
        bgcolor: active ? ACCENT : disabled ? "#f8fafc" : "#fff",
        color: active ? "#fff" : disabled ? "#cbd5e1" : "#0f172a",
        textDecoration: disabled ? "line-through" : "none",
        fontWeight: 700, textAlign: "center", userSelect: "none",
        transition: "all 0.12s ease",
        "&:hover": disabled || active ? {} : { borderColor: ACCENT },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function Reserve() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { profile } = useContext(UserContext);

  const { data: rData, loading: rLoading } = useQuery(RESTAURANT, { variables: { slug }, skip: !slug });
  const restaurant = rData?.restaurant;
  const restaurantId = restaurant?._id;

  const { data: sData, loading: sLoading, error: sError } = useQuery(SETTINGS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: "network-only",
  });
  const settings = sData?.publicReservationSettings;

  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(null);
  const [startsAt, setStartsAt] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(null);
  const opId = useRef(v4());

  useEffect(() => {
    if (settings?.today && !date) setDate(settings.today);
  }, [settings, date]);

  useEffect(() => {
    if (profile && !prefilled) {
      setName(profile.name || "");
      setPhone(profile.phone || "");
      setEmail(profile.email || "");
      setPrefilled(true);
    }
  }, [profile, prefilled]);

  const { data: slotData, loading: slotsLoading, refetch: refetchSlots } = useQuery(SLOTS, {
    variables: { restaurantId, date, partySize },
    skip: !restaurantId || !date || !settings?.enabled,
    fetchPolicy: "network-only",
  });
  const slots = slotData?.reservationSlots || [];
  const openSlots = slots.filter((s) => s.available);

  const [create, { loading: saving }] = useMutation(CREATE);

  const days = useMemo(
    () => (settings?.today ? Array.from({ length: Math.min(60, settings.daysAhead || 30) + 1 }, (_, i) => addDaysKey(settings.today, i)) : []),
    [settings]
  );
  const partyOptions = useMemo(
    () => Array.from({ length: Math.max(1, Math.min(12, settings?.maxPartySize || 8)) }, (_, i) => i + 1),
    [settings]
  );
  const chosen = slots.find((s) => s.startsAt === startsAt);

  const choosePartySize = (n) => { setPartySize(n); setStartsAt(null); setError(""); };
  const chooseDate = (k) => { setDate(k); setStartsAt(null); setError(""); };

  const submit = async () => {
    setError("");
    if (!startsAt) return setError("Pick a time first.");
    if (!name.trim()) return setError("Add the name the table is under.");
    try {
      const { data } = await create({
        variables: {
          restaurantId,
          partySize,
          startsAt,
          guestName: name.trim(),
          guestPhone: phone.trim() || null,
          guestEmail: email.trim() || null,
          notes: notes.trim() || null,
          clientOpId: opId.current,
        },
      });
      opId.current = v4();
      setBooked(data.createWebReservation);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errText(e));
      // A taken time comes back as a refusal: show what is still open.
      if (e?.graphQLErrors?.length) {
        setStartsAt(null);
        refetchSlots();
      } else {
        opId.current = v4();
      }
    }
  };

  const hero = (
    <Box sx={{
      background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)",
      pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 },
    }}>
      <Container maxWidth="lg">
        <Button
          component={RouterLink}
          to={restaurant?.slug ? `/restaurant/${restaurant.slug}` : "/restaurant-list"}
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ color: "rgba(255,255,255,0.8)", textTransform: "none", fontWeight: 700, mb: 1.5, px: 0 }}
        >
          Back to the menu
        </Button>
        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{
            width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: "14px",
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <EventSeatRoundedIcon sx={{ color: "#fecaca", fontSize: 28 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" }, color: "#fff", lineHeight: 1.15 }}>
              {booked ? "You’re booked" : "Reserve a table"}
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.75)", mt: 0.4 }} noWrap>
              {restaurant?.name || settings?.restaurantName || ""}
              {restaurant?.address ? ` · ${restaurant.address}` : ""}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );

  const loadingAll = rLoading || (restaurantId && sLoading && !settings);

  return (
    <>
      <Header />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
        {hero}
        <Container maxWidth="lg" sx={{ mt: -4, pb: 8, position: "relative", zIndex: 2 }}>
          {loadingAll ? (
            <Paper sx={{ p: 6, borderRadius: "18px", display: "flex", justifyContent: "center" }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Paper>
          ) : !restaurant ? (
            <Paper sx={{ p: 5, borderRadius: "18px", textAlign: "center" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>{"We couldn’t find that restaurant"}</Typography>
              <Button onClick={() => navigate("/restaurant-list")} sx={{ mt: 2, textTransform: "none", fontWeight: 700, color: ACCENT }}>
                See all restaurants
              </Button>
            </Paper>
          ) : sError || !settings?.enabled ? (
            <Paper sx={{ p: 5, borderRadius: "18px", textAlign: "center" }}>
              <EventSeatRoundedIcon sx={{ fontSize: 44, color: "#cbd5e1" }} />
              <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", mt: 1 }}>
                {`${restaurant.name} isn’t taking reservations online right now`}
              </Typography>
              <Typography sx={{ color: "#64748b", mt: 0.5 }}>You can still order for pickup or delivery.</Typography>
              <Button component={RouterLink} to={`/restaurant/${restaurant.slug}`} variant="contained" disableElevation
                sx={{ mt: 2.5, textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: ACCENT, "&:hover": { bgcolor: "#E02A06" } }}>
                Back to the menu
              </Button>
            </Paper>
          ) : booked ? (
            <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: "18px", maxWidth: 620, mx: "auto", textAlign: "center" }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 56, color: "#16a34a" }} />
              <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", mt: 1 }}>See you soon, {booked.guestName.split(" ")[0]}</Typography>
              <Typography sx={{ color: "#475569", mt: 1 }}>
                A table for {booked.partySize} at {restaurant.name}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", mt: 0.5 }}>
                {new Date(booked.reservedFor).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </Typography>
              <Box sx={{ mt: 3, p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px dashed #cbd5e1", display: "inline-block" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Confirmation code
                </Typography>
                <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, letterSpacing: 2, color: "#0f172a" }}>
                  {booked.confirmationCode}
                </Typography>
              </Box>
              {!!email.trim() && (
                <Typography sx={{ color: "#64748b", mt: 2, fontSize: "0.9rem" }}>We sent the details to {email.trim()}.</Typography>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="center" sx={{ mt: 3 }}>
                <Button component={RouterLink} to="/reservations" variant="contained" disableElevation
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: ACCENT, "&:hover": { bgcolor: "#E02A06" } }}>
                  My reservations
                </Button>
                <Button component={RouterLink} to={`/restaurant/${restaurant.slug}`} variant="outlined"
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: "#0f172a", borderColor: "#cbd5e1" }}>
                  Back to the menu
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: "18px", border: "1px solid #e5e7eb", boxShadow: "0 6px 18px rgba(15,23,42,0.06)" }}>
                  <SectionTitle n={1} icon={GroupRoundedIcon}>How many people?</SectionTitle>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                    {partyOptions.map((n) => (
                      <Pick key={n} active={partySize === n} onClick={() => choosePartySize(n)} sx={{ minWidth: 48 }}>{n}</Pick>
                    ))}
                  </Stack>
                  {(settings.maxPartySize || 0) <= 12 && (
                    <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mb: 3 }}>
                      Booking for more than {settings.maxPartySize}? Contact the restaurant directly.
                    </Typography>
                  )}

                  <SectionTitle n={2} icon={CalendarMonthRoundedIcon}>Which day?</SectionTitle>
                  <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 3, "&::-webkit-scrollbar": { height: 6 } }}>
                    {days.map((k) => {
                      const p = dayParts(k);
                      return (
                        <Pick key={k} active={date === k} onClick={() => chooseDate(k)} sx={{ minWidth: 76, flexShrink: 0, py: 1.1 }}>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, opacity: 0.85 }}>
                            {k === settings.today ? "Today" : k === addDaysKey(settings.today, 1) ? "Tomorrow" : p.week}
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 800 }}>{p.day}</Typography>
                        </Pick>
                      );
                    })}
                  </Box>

                  <SectionTitle n={3} icon={AccessTimeRoundedIcon}>What time?</SectionTitle>
                  {slotsLoading ? (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}><CircularProgress size={28} sx={{ color: ACCENT }} /></Box>
                  ) : !slots.length ? (
                    <Typography sx={{ color: "#64748b", mb: 3 }}>No tables on {date ? dayParts(date).long : "that day"}. Try another day.</Typography>
                  ) : (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)", md: "repeat(6, 1fr)" }, gap: 1, mb: 1 }}>
                        {slots.map((s) => (
                          <Pick key={s.startsAt} active={startsAt === s.startsAt} disabled={!s.available} onClick={() => { setStartsAt(s.startsAt); setError(""); }}>
                            {s.time}
                          </Pick>
                        ))}
                      </Box>
                      {!openSlots.length && (
                        <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>Every table is taken that day. Try another day or a smaller group.</Typography>
                      )}
                    </>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <SectionTitle n={4} icon={EventSeatRoundedIcon}>Your details</SectionTitle>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Name for the table" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputProps={{ maxLength: 24 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth type="email" label="Email for your confirmation" value={email} onChange={(e) => setEmail(e.target.value)} inputProps={{ maxLength: 120 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} label="Anything the restaurant should know? (optional)" placeholder="Birthday, high chair, allergies" value={notes} onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: "18px", border: "1px solid #e5e7eb", position: { md: "sticky" }, top: { md: 96 } }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 2 }}>Your reservation</Typography>
                  <Stack gap={1.25}>
                    <Stack direction="row" gap={1} alignItems="center">
                      <GroupRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      <Typography sx={{ fontWeight: 700 }}>{partySize} {partySize === 1 ? "person" : "people"}</Typography>
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                      <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      <Typography sx={{ fontWeight: 700 }}>{date ? dayParts(date).long : "Pick a day"}</Typography>
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                      <AccessTimeRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      <Typography sx={{ fontWeight: 700, color: chosen ? "#0f172a" : "#94a3b8" }}>{chosen ? chosen.time : "Pick a time"}</Typography>
                    </Stack>
                  </Stack>
                  {!!error && <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>{error}</Alert>}
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={saving || !startsAt || !name.trim()}
                    onClick={submit}
                    sx={{ mt: 2.5, py: 1.4, textTransform: "none", fontWeight: 800, fontSize: "1rem", borderRadius: "12px", bgcolor: ACCENT, "&:hover": { bgcolor: "#E02A06" } }}
                  >
                    {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Book the table"}
                  </Button>
                  <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", mt: 1.5, textAlign: "center" }}>
                    Free to book. Cancel any time from My reservations.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
}
